<template>
  <b-card>
    <template #header>
      <div class="d-flex justify-content-between align-items-center">
        <span><i class="fa fa-ellipsis-v mr-2 drag-handle"></i><i class="icon-lock"></i> <strong>Ban Management</strong></span>
        <div class="d-flex align-items-center">
          <div v-if="isBanned" class="mr-3 text-right">
            <b-badge variant="danger" class="p-2 mb-1 d-block">
              {{ banTypeLabel(userData.banned) }}
            </b-badge>
            <small class="text-muted d-block" style="max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" :title="userData.banInfo">
              {{ userData.banInfo || 'No reason' }}
            </small>
          </div>
          <b-badge v-else variant="success" class="p-2">
            CURRENTLY UNBANNED
          </b-badge>
        </div>
      </div>
    </template>

    <b-form @submit.prevent="performBan">

      <b-form-group label="Reason" label-for="ban-reason">
        <b-input-group>
          <b-input-group-prepend is-text>
            <i class="fa fa-pencil"></i>
          </b-input-group-prepend>
          <b-form-input
            id="ban-reason"
            v-model="reason"
            placeholder="Enter reason for ban or overwrite..."
            :disabled="userLock"
            required
          />
        </b-input-group>
      </b-form-group>

      <b-row>
        <b-col md="6">
          <b-form-group label="Ban Type" label-for="ban-type">
            <b-input-group>
              <b-input-group-prepend is-text>
                <i class="fa fa-gavel"></i>
              </b-input-group-prepend>
              <b-form-select id="ban-type" v-model="selectBan" :disabled="userLock">
                <template #first>
                  <option value="" disabled>Select Type</option>
                </template>
                <option value="P">Permanent</option>
                <option value="T">Temporary</option>
                <option value="E">Trade</option>
                <option value="M">Quiet</option>
                <option value="X">PVP</option>
              </b-form-select>
            </b-input-group>
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group label="Duration (Days)" label-for="ban-days">
            <b-input-group>
              <b-input-group-prepend is-text>
                <i class="fa fa-clock-o"></i>
              </b-input-group-prepend>
              <b-form-input
                id="ban-days"
                type="number"
                v-model="days"
                :disabled="isDurationDisabled || userLock"
                placeholder="0"
                min="0"
              />
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>

      <hr />

      <div class="d-flex justify-content-between mt-3">
        <b-button
          v-if="isBanned"
          variant="success"
          @click="performUnban"
          :disabled="userLock || !reason || !hasPermission(UserBan)"
        >
          <i class="fa fa-unlock"></i> Unban User
        </b-button>
        <div v-else></div>

        <b-button
          :variant="isBanned ? 'warning' : 'danger'"
          type="submit"
          :disabled="!isValidBanAction"
        >
          <i class="fa fa-ban"></i>
          {{ isBanned ? 'Overwrite / Update Ban' : 'Ban User' }}
        </b-button>
      </div>

    </b-form>
  </b-card>
</template>

<script>
import { mapActions } from 'vuex'
import moment from 'moment'

export default {
  name: 'BanManagement',
  props: ['userData', 'userLock'],
  data() {
    return {
      reason: '',
      selectBan: '',
      days: 0
    }
  },
  computed: {
    isBanned() {
      if (!this.userData.banned || this.userData.banned === '' || this.userData.banned === null) return false;
      
      // Permanent bans never expire
      if (this.userData.banned === 'P') return true;
      
      // Check if BannedUntil exists and if it's in the future
      const bannedUntilStr = this.userData.banned_until && typeof this.userData.banned_until === 'object' 
        ? this.userData.banned_until.String 
        : this.userData.banned_until;

      if (bannedUntilStr && bannedUntilStr !== '' && !bannedUntilStr.startsWith('0001')) {
        return moment(bannedUntilStr).isAfter(moment());
      }
      
      // If no BannedUntil but has a ban code (and not P), assume it's still active or fallback
      return true;
    },
    isDurationDisabled() {
      return ['P', 'S', 'A', ''].includes(this.selectBan);
    },
    isValidBanAction() {
      if (this.userLock) return false;
      if (!this.reason || this.reason.trim().length === 0) return false;
      if (!this.hasPermission(this.UserBan)) return false;

      if (!this.selectBan) return false;
      if (!['P', 'A'].includes(this.selectBan) && Number(this.days) <= 0) return false;

      return true;
    }
  },
  methods: {
    ...mapActions(['setLock']),

    banTypeLabel(type) {
      const labels = {
        'P': 'Permanently Banned',
        'T': 'Temporarily Banned',
        'E': 'Trade Banned',
        'M': 'Quiet Banned',
        'A': 'MAC Banned',
        'X': 'PVP Banned',
        'S': 'Single-player Banned'
      };
      return labels[type] || 'Banned (' + type + ')';
    },

    performBan() {
      if (!this.isValidBanAction) return;
      this.emit('banUser', {
        username: this.userData.username,
        type: this.selectBan,
        days: Number(this.days),
        servers: [],
        reason: this.reason
      });
      this.setLock(true);
    },

    performUnban() {
      if (!this.reason || !this.hasPermission(this.UserBan)) return;
      this.emit('unBan', {
        username: this.userData.username,
        servers: [],
        reason: this.reason
      });
      this.setLock(true);
    }
  }
}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
